import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import {defineMessages, useIntl} from 'react-intl';

import BurnCenterDeviceSelector from '../burn-center-device-selector/burn-center-device-selector.jsx';
import DomiCarBurnCenter from '../domi-car-burn-center/domi-car-burn-center.jsx';
import DomiControllerSimulator from '../domi-controller-simulator/domi-controller-simulator.jsx';
import GreenFlag from '../green-flag/green-flag.jsx';
import MicroBitBurnCenter from '../microbit-burn-center/microbit-burn-center.jsx';
import StopAll from '../stop-all/stop-all.jsx';
import TurboMode from '../turbo-mode/turbo-mode.jsx';
import uploadIcon from '../action-menu/icon--file-upload.svg';
import controllerIcon from '../../lib/libraries/extensions/domiController/domi-controller-small.svg';

import styles from './controls.css';

const messages = defineMessages({
    goTitle: {
        id: 'gui.controls.go',
        defaultMessage: 'Go',
        description: 'Green flag button title'
    },
    stopTitle: {
        id: 'gui.controls.stop',
        defaultMessage: 'Stop',
        description: 'Stop button title'
    },
    domiBurnCenter: {
        id: 'gui.domiCar.upload',
        defaultMessage: 'Burn Center',
        description: 'Button that opens the Domi Car Scratch program burn center'
    },
    controllerSimulator: {
        id: 'gui.domiControllerSimulator.open',
        defaultMessage: 'Controller',
        description: 'Button that opens the Domi controller simulator'
    }
});

const Controls = function (props) {
    const {
        active,
        burnCenterAvailableDevices,
        burnCenterConnected,
        burnCenterDevice,
        burnCenterInstructionCount,
        burnCenterInstructions,
        burnCenterMessage,
        burnCenterOpen,
        burnCenterPhase,
        burnCenterProgress,
        burnCenterSelectorOpen,
        burnCenterWebUsbSupported,
        className,
        controllerActiveButtons,
        controllerConnected,
        controllerEventHistory,
        controllerSimulatorAvailable,
        controllerSimulatorOpen,
        isFullScreen,
        onBurnCenterClose,
        onBurnCenterCompile,
        onBurnCenterDeviceSelect,
        onBurnCenterOpen,
        onBurnCenterStart,
        onControllerButtonPress,
        onControllerButtonRelease,
        onControllerEmergencyStop,
        onControllerSimulatorClose,
        onControllerSimulatorOpen,
        onGreenFlagClick,
        onStopAllClick,
        turbo,
        ...componentProps
    } = props;
    const intl = useIntl();
    return (
        <div
            className={classNames(styles.controlsContainer, className)}
            {...componentProps}
        >
            <GreenFlag
                active={active}
                title={intl.formatMessage(messages.goTitle)}
                onClick={onGreenFlagClick}
                isFullScreen={isFullScreen}
            />
            <StopAll
                active={active}
                title={intl.formatMessage(messages.stopTitle)}
                onClick={onStopAllClick}
                isFullScreen={isFullScreen}
            />
            {!isFullScreen && (
                <div className={styles.domiUploadWrapper}>
                    <button
                        aria-label={intl.formatMessage(messages.domiBurnCenter)}
                        className={styles.domiUploadButton}
                        type="button"
                        onClick={onBurnCenterOpen}
                    >
                        <img
                            alt=""
                            className={styles.domiUploadIcon}
                            draggable={false}
                            src={uploadIcon}
                        />
                        {intl.formatMessage(messages.domiBurnCenter)}
                    </button>
                    <BurnCenterDeviceSelector
                        availableDevices={burnCenterAvailableDevices}
                        isOpen={burnCenterSelectorOpen}
                        onClose={onBurnCenterClose}
                        onSelect={onBurnCenterDeviceSelect}
                    />
                    {burnCenterDevice === 'microbit' ? (
                        <MicroBitBurnCenter
                            instructionCount={burnCenterInstructionCount}
                            instructions={burnCenterInstructions}
                            isOpen={burnCenterOpen}
                            message={burnCenterMessage}
                            phase={burnCenterPhase}
                            progress={burnCenterProgress}
                            webUsbSupported={burnCenterWebUsbSupported}
                            onClose={onBurnCenterClose}
                            onCompile={onBurnCenterCompile}
                            onStart={onBurnCenterStart}
                        />
                    ) : burnCenterDevice === 'domiCar' ? (
                        <DomiCarBurnCenter
                            connected={burnCenterConnected}
                            instructionCount={burnCenterInstructionCount}
                            instructions={burnCenterInstructions}
                            isOpen={burnCenterOpen}
                            message={burnCenterMessage}
                            phase={burnCenterPhase}
                            progress={burnCenterProgress}
                            onClose={onBurnCenterClose}
                            onStart={onBurnCenterStart}
                        />
                    ) : null}
                </div>
            )}
            {!isFullScreen && controllerSimulatorAvailable ? (
                <div className={styles.controllerSimulatorWrapper}>
                    <button
                        aria-label={intl.formatMessage(messages.controllerSimulator)}
                        className={styles.controllerSimulatorButton}
                        type="button"
                        onClick={onControllerSimulatorOpen}
                    >
                        <img
                            alt=""
                            className={styles.controllerSimulatorIcon}
                            draggable={false}
                            src={controllerIcon}
                        />
                        {intl.formatMessage(messages.controllerSimulator)}
                    </button>
                    <DomiControllerSimulator
                        activeButtons={controllerActiveButtons}
                        connected={controllerConnected}
                        eventHistory={controllerEventHistory}
                        isOpen={controllerSimulatorOpen}
                        onButtonPress={onControllerButtonPress}
                        onButtonRelease={onControllerButtonRelease}
                        onClose={onControllerSimulatorClose}
                        onEmergencyStop={onControllerEmergencyStop}
                    />
                </div>
            ) : null}
            {turbo ? (
                <TurboMode />
            ) : null}
        </div>
    );
};

Controls.propTypes = {
    active: PropTypes.bool,
    burnCenterAvailableDevices: PropTypes.arrayOf(PropTypes.oneOf(['domiCar', 'microbit'])),
    burnCenterConnected: PropTypes.bool,
    burnCenterDevice: PropTypes.oneOf(['domiCar', 'microbit']),
    burnCenterInstructionCount: PropTypes.number,
    burnCenterInstructions: PropTypes.arrayOf(PropTypes.string),
    burnCenterMessage: PropTypes.string,
    burnCenterOpen: PropTypes.bool,
    burnCenterPhase: PropTypes.oneOf(['preparing', 'ready', 'invalid', 'uploading', 'success', 'error']),
    burnCenterProgress: PropTypes.number,
    burnCenterSelectorOpen: PropTypes.bool,
    burnCenterWebUsbSupported: PropTypes.bool,
    className: PropTypes.string,
    controllerActiveButtons: PropTypes.arrayOf(PropTypes.string),
    controllerConnected: PropTypes.bool,
    controllerEventHistory: PropTypes.arrayOf(PropTypes.shape({
        action: PropTypes.string.isRequired,
        button: PropTypes.string.isRequired,
        id: PropTypes.number.isRequired
    })),
    controllerSimulatorAvailable: PropTypes.bool,
    controllerSimulatorOpen: PropTypes.bool,
    isFullScreen: PropTypes.bool,
    onBurnCenterClose: PropTypes.func,
    onBurnCenterCompile: PropTypes.func,
    onBurnCenterDeviceSelect: PropTypes.func,
    onBurnCenterOpen: PropTypes.func,
    onBurnCenterStart: PropTypes.func,
    onControllerButtonPress: PropTypes.func,
    onControllerButtonRelease: PropTypes.func,
    onControllerEmergencyStop: PropTypes.func,
    onControllerSimulatorClose: PropTypes.func,
    onControllerSimulatorOpen: PropTypes.func,
    onGreenFlagClick: PropTypes.func.isRequired,
    onStopAllClick: PropTypes.func.isRequired,
    turbo: PropTypes.bool
};

Controls.defaultProps = {
    active: false,
    burnCenterAvailableDevices: [],
    burnCenterConnected: false,
    burnCenterDevice: null,
    burnCenterInstructionCount: 0,
    burnCenterInstructions: [],
    burnCenterMessage: '',
    burnCenterOpen: false,
    burnCenterPhase: 'preparing',
    burnCenterProgress: 0,
    burnCenterSelectorOpen: false,
    burnCenterWebUsbSupported: false,
    controllerActiveButtons: [],
    controllerConnected: false,
    controllerEventHistory: [],
    controllerSimulatorAvailable: false,
    controllerSimulatorOpen: false,
    onBurnCenterClose: () => {},
    onBurnCenterCompile: () => {},
    onBurnCenterDeviceSelect: () => {},
    onBurnCenterOpen: () => {},
    onBurnCenterStart: () => {},
    onControllerButtonPress: () => {},
    onControllerButtonRelease: () => {},
    onControllerEmergencyStop: () => {},
    onControllerSimulatorClose: () => {},
    onControllerSimulatorOpen: () => {},
    turbo: false
};

export default Controls;
